import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { BotTerminalLines, BotTerminalLine } from '/imports/api/botTerminal';
import { terminalSelector, filterBotOptions } from '/imports/api/terminalFilter';
import { Publications } from '/imports/publications';
import { useSubscribe, useFind, useTracker } from '/imports/hooks';

const fmtTime = (d: Date) => {
  const t = d instanceof Date ? d : new Date(d);
  return t.toTimeString().slice(0, 8);
};

interface Props {
  bot?: string;
  max?: number;
}

export const BotTerminal = ({ bot, max = 500 }: Props) => {
  const isLoading = useSubscribe(Publications.botTerminal);
  // The filter runs against the client-side collection, so it applies to the
  // lines already on screen AND to lines arriving while it is active -- the
  // subscription itself keeps streaming everything. Typed text narrows by
  // substring; a name picked from the list pins an exact match.
  const [botFilter, setBotFilter] = useState('');
  const [botExact, setBotExact] = useState(false);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const selector = terminalSelector({ bot: botFilter, botExact }, { bot });
  const lines = useFind(
    () => BotTerminalLines.find(selector, { sort: { timestamp: 1 }, limit: max }) as any,
    [bot, botFilter, botExact, max],
  ) as BotTerminalLine[];

  // Picklist options are the distinct bot names already in the stream: no
  // extra subscription, and only bots with something in the log are offered.
  const allNames = useTracker(
    () => [...new Set(BotTerminalLines.find({}, { fields: { bot: 1 } }).fetch().map((l) => l.bot))],
    [],
  ) as string[];
  // A pinned pick reopens onto the full list, not just its own name.
  const options = filterBotOptions(allNames, botExact ? '' : botFilter);
  const hi = Math.min(highlight, Math.max(0, options.length - 1));

  const pick = (name: string) => {
    setBotFilter(name);
    setBotExact(true);
    setOpen(false);
  };
  const clearFilter = () => {
    setBotFilter('');
    setBotExact(false);
    setOpen(false);
  };

  // The dropdown portals to <body> at the input's screen position: the
  // terminal wrapper is overflow-hidden (and often collapsed to 7rem), so
  // anything rendered inside it gets clipped at the terminal's edge.
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [menuRect, setMenuRect] = useState<{ top: number; left: number; width: number } | null>(null);
  useEffect(() => {
    if (!open) { setMenuRect(null); return; }
    const update = () => {
      const r = inputRef.current?.getBoundingClientRect();
      if (r) setMenuRect({ top: r.bottom + 2, left: r.left, width: r.width });
    };
    update();
    // Capture-phase scroll: the page scrolls in nested containers, and a
    // fixed-position menu must track the input through all of them.
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [open]);

  const [expanded, setExpanded] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    el.style.height = expanded ? '30rem' : '7rem';
  }, [expanded]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [lines.length]);

  return (
    <div
      ref={wrapperRef}
      style={{ height: '4rem' }}
      className="flex max-h-[80vh] min-h-8 resize-y flex-col overflow-hidden rounded-[4px] border border-[#222] bg-[#0c0c0c] font-[ui-monospace,'SF_Mono',Menlo,Consolas,monospace] text-[12px] leading-[1.4] text-[#d8d8d8] [&_.lvl1]:text-[#f0f0f0] [&_.lvl2]:text-[#b0b0b0] [&_.lvl3]:text-[#6b6b6b]"
    >
      <div className="toolbar flex flex-none items-center gap-1.5 border-b border-[#222] bg-[#161616] px-1.5 py-0.5">
        {/* Hidden when the terminal is already hard-scoped to one bot (the
            bot page): filtering within a single bot's stream by bot name is
            a no-op by construction. */}
        {!bot && (
          <>
            <div>
              <input
                ref={inputRef}
                value={botFilter}
                placeholder="filter by bot…"
                spellCheck={false}
                role="combobox"
                aria-expanded={open && options.length > 0}
                onChange={(e) => {
                  setBotFilter(e.target.value);
                  setBotExact(false);
                  setOpen(true);
                  setHighlight(0);
                }}
                onFocus={() => setOpen(true)}
                onBlur={() => setOpen(false)}
                onKeyDown={(e) => {
                  if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setHighlight(Math.min(hi + 1, options.length - 1)); }
                  else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight(Math.max(hi - 1, 0)); }
                  else if (e.key === 'Enter' && open && options[hi]) { e.preventDefault(); pick(options[hi]!); }
                  else if (e.key === 'Escape') { open ? setOpen(false) : clearFilter(); }
                }}
                className="w-40 rounded-[3px] border border-[#2a2a2a] bg-[#0c0c0c] px-1.5 py-0.5 font-[inherit] text-[11px] text-[#d8d8d8] outline-none placeholder:text-[#6b6b6b] focus:border-[#3a3a3a]"
              />
              {open && options.length > 0 && menuRect && createPortal(
                <ul
                  style={{ position: 'fixed', top: menuRect.top, left: menuRect.left, width: menuRect.width }}
                  className="z-50 m-0 max-h-56 list-none overflow-y-auto rounded-[3px] border border-[#2a2a2a] bg-[#161616] p-0.5 font-[ui-monospace,'SF_Mono',Menlo,Consolas,monospace]"
                >
                  {options.map((name, i) => (
                    <li
                      key={name}
                      // mousedown, not click: click lands after the input's
                      // blur has already closed the list.
                      onMouseDown={(e) => { e.preventDefault(); pick(name); }}
                      onMouseEnter={() => setHighlight(i)}
                      className={`cursor-pointer rounded-[2px] px-1.5 py-0.5 text-[11px] ${i === hi ? 'bg-[#232323] text-[#f0f0f0]' : 'text-[#b0b0b0]'}`}
                    >
                      {name}
                    </li>
                  ))}
                </ul>,
                document.body,
              )}
            </div>
            {botFilter && (
              <button
                title="Clear filter (Esc)"
                className="cursor-pointer rounded-[3px] border-0 bg-transparent px-1.5 py-0.5 font-[inherit] text-[11px] text-[#b0b0b0] hover:bg-[#232323] hover:text-[#f0f0f0]"
                onClick={clearFilter}
              >
                ✕ Clear
              </button>
            )}
          </>
        )}
        <button
          className="ml-auto cursor-pointer rounded-[3px] border-0 bg-transparent px-1.5 py-0.5 font-[inherit] text-[11px] text-[#b0b0b0] hover:bg-[#232323] hover:text-[#f0f0f0]"
          onClick={() => setExpanded((e) => !e)}
        >
          {expanded ? '▾ Collapse' : '▸ Expand'}
        </button>
      </div>
      <div className="scroll flex-1 overflow-y-auto px-2.5 py-1.5 break-words whitespace-pre-wrap" ref={scrollRef}>
        {isLoading() ? (
          <span className="line block">Connecting…</span>
        ) : lines.length === 0 && botFilter ? (
          <span className="line block text-[#6b6b6b]">no log lines match “{botFilter.trim()}”</span>
        ) : (
          lines.map((l) => (
            <span key={l._id} className={`line block lvl${l.level}`}>
              <span className="ts mr-1.5 text-[#6b6b6b]">{fmtTime(l.timestamp)}</span>
              <span className="bot mr-1.5 text-[#7fc8ff]">[{l.bot}]</span>
              {l.entry}
            </span>
          ))
        )}
      </div>
    </div>
  );
};
